import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { toDateInput } from "../dates";
import { logoUrl, renderDocument } from "../documents";
import { invoiceData, openPrintWindow, type InvoiceEntry } from "../printing";
import { DateField } from "./DateField";
import { Modal } from "./Modal";
import type { NotificationKind } from "./Portal";

interface DraftDates { issuedDate: string; dueDate: string }

const draftsOf = (entries: InvoiceEntry[]): Record<string, DraftDates> =>
  Object.fromEntries(entries.map((entry) => [entry.invoice.id, { issuedDate: toDateInput(entry.invoice.issuedAt), dueDate: toDateInput(entry.invoice.dueAt) }]));

/** Invoices used to print the moment they were raised, so a wrong date could only
 *  be found on paper. The preview renders through the very same template the print
 *  window uses, which makes what is approved here exactly what comes out. */
export function InvoicePreviewModal({ entries, onClose, onMessage, onSaved }: {
  entries: InvoiceEntry[];
  onClose: () => void;
  onMessage: (message: string, kind?: NotificationKind) => void;
  onSaved: () => void;
}) {
  const [current, setCurrent] = useState(entries);
  const [drafts, setDrafts] = useState<Record<string, DraftDates>>(() => draftsOf(entries));
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);

  // The preview follows the fields, not the saved record, so staff see the date
  // they are about to commit rather than the one being replaced.
  const previewEntries = useMemo(() => current.map((entry) => {
    const draft = drafts[entry.invoice.id];
    if (!draft) return entry;
    return { ...entry, invoice: { ...entry.invoice, issuedAt: draft.issuedDate || entry.invoice.issuedAt, dueAt: draft.dueDate || null } };
  }), [current, drafts]);
  const signature = JSON.stringify(previewEntries.map((entry) => [entry.invoice.id, entry.invoice.issuedAt, entry.invoice.dueAt]));

  useEffect(() => {
    let live = true;
    logoUrl()
      .then((logo) => renderDocument("invoice", "Invoices", previewEntries.map((entry) => invoiceData(entry, logo))))
      .then((rendered) => { if (live) setHtml(rendered); })
      .catch(() => { if (live) setHtml(""); });
    return () => { live = false; };
  }, [signature]);

  function update(id: string, field: keyof DraftDates, value: string) {
    setDrafts((previous) => ({ ...previous, [id]: { ...previous[id]!, [field]: value } }));
  }

  async function saveAndPrint() {
    if (current.some((entry) => !drafts[entry.invoice.id]?.issuedDate)) throw new Error("Every invoice needs an invoice date.");
    setBusy(true);
    try {
      const updated: InvoiceEntry[] = [];
      let changed = 0;
      for (const entry of current) {
        const draft = drafts[entry.invoice.id]!;
        if (draft.issuedDate === toDateInput(entry.invoice.issuedAt) && draft.dueDate === toDateInput(entry.invoice.dueAt)) { updated.push(entry); continue; }
        updated.push({ ...entry, invoice: await api.setInvoiceDates(entry.invoice.id, draft.issuedDate, draft.dueDate || null) });
        changed++;
      }
      setCurrent(updated);
      setDrafts(draftsOf(updated));
      const logo = await logoUrl();
      const printable = await renderDocument("invoice", "Invoices", updated.map((entry) => invoiceData(entry, logo)));
      if (!openPrintWindow(printable)) { onMessage("Enable pop-ups to print invoices.", "error"); return; }
      if (changed) onSaved();
      onMessage(changed
        ? `${changed} invoice date${changed === 1 ? "" : "s"} saved; printing ${updated.length}.`
        : `Printing ${updated.length} invoice${updated.length === 1 ? "" : "s"}.`);
      onClose();
    } finally { setBusy(false); }
  }

  return <Modal title={current.length === 1 ? `Invoice ${current[0]!.invoice.number}` : `${current.length} invoices`} onClose={onClose} wide>
    <div className="invoice-preview">
      <div className="invoice-date-rows">
        {current.map((entry) => {
          const draft = drafts[entry.invoice.id]!;
          return <div key={entry.invoice.id} className="invoice-date-row">
            <strong>{entry.invoice.number}</strong>
            <label className="field"><span>Invoice date</span><DateField label={`Invoice date for ${entry.invoice.number}`} value={draft.issuedDate} onChange={(value) => update(entry.invoice.id, "issuedDate", value)}/></label>
            <label className="field"><span>Due date</span><DateField label={`Due date for ${entry.invoice.number}`} value={draft.dueDate} onChange={(value) => update(entry.invoice.id, "dueDate", value)}/></label>
          </div>;
        })}
        <small className="field-hint">The dates are saved to the invoice when you print.</small>
      </div>
      <iframe className="invoice-preview-frame" title="Invoice preview" srcDoc={html}/>
      <div className="modal-actions">
        <button type="button" className="secondary-button" onClick={onClose}>Close</button>
        <button type="button" className="save-button" disabled={busy} onClick={() => saveAndPrint().catch((error: Error) => onMessage(error.message, "error"))}>{busy ? "Saving…" : "Save dates & print"}</button>
      </div>
    </div>
  </Modal>;
}
